import { ToastMessage } from '@/components'
import PropTypes from 'prop-types'
import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import PlaidInstantAuthFailedModal from './plaid-instant-auth-failed-modal'
import PlaidLink from './plaid-link'

// Plaid Link wrapper component that includes retry modal
const PlaidLinkWithRetry = ({ onSuccess, onExit, children }) => {
  const [showRetryModal, setShowRetryModal] = useState(false)
  const [isContinueLoading, setIsContinueLoading] = useState(false)
  const openAuthOnlyRef = useRef()
  const navigate = useNavigate()

  const handleExit = useCallback((err, metadata) => {
    console.log('Plaid Link exit:', err, metadata)

    if (err != null) {
      toast.custom(() => (
        <ToastMessage
          text="Something went wrong"
          description="Unfortunately, we encountered an unexpected issue. Please try again, or use the 'Contact Support' button below for assistance. We're here to help you resolve this as smoothly as possible."
        />
      ))
    }

    // Reset the loading state
    setIsContinueLoading(false)
    setShowRetryModal(true)
  }, [])

  const handlePayByCheckWrapper = () => {
    setShowRetryModal(false)
    navigate('/payments/pay-by-check')
  }

  const handleContinueWrapper = () => {
    setIsContinueLoading(true)
    setShowRetryModal(false)
    // Open auth-only link after modal closes
    setTimeout(() => {
      if (openAuthOnlyRef.current) {
        openAuthOnlyRef.current()
      }
    }, 100)
  }

  const handleAuthOnlySuccess = useCallback(
    (public_token, payload) => {
      setIsContinueLoading(false)
      if (onSuccess) {
        onSuccess(public_token, payload)
      }
    },
    [onSuccess]
  )

  const handleAuthOnlyExit = useCallback(
    (err, metadata) => {
      setIsContinueLoading(false)
      if (onExit) {
        onExit(err, metadata)
      }
    },
    [onExit]
  )

  return (
    <>
      {/* Main Plaid Link (non-auth-only) */}
      <PlaidLink onSuccess={handleAuthOnlySuccess} onExit={handleExit} authOnly={false}>
        {children}
      </PlaidLink>

      {/* Retry Modal */}
      {showRetryModal && (
        <PlaidInstantAuthFailedModal
          isOpen={showRetryModal}
          onClose={() => {
            setShowRetryModal(false)
            // Call onExit callback if provided
            // This is still exiting the process, even though it's not
            // exiting directly from the Plaid Link flow
            if (onExit) {
              onExit(null, null)
            }
          }}
          onPayByCheck={handlePayByCheckWrapper}
          onContinue={handleContinueWrapper}
          isContinueLoading={isContinueLoading}
        />
      )}

      {/* Auth-only Plaid Link (always rendered, but only used when needed) */}
      <PlaidLink onSuccess={handleAuthOnlySuccess} onExit={handleAuthOnlyExit} authOnly={true}>
        {({ open, ready }) => {
          // Store the open function in the ref
          openAuthOnlyRef.current = open

          return (
            <div style={{ display: 'none' }}>
              {/* Hidden trigger that gets called programmatically */}
              <button onClick={open} disabled={!ready} />
            </div>
          )
        }}
      </PlaidLink>
    </>
  )
}

PlaidLinkWithRetry.propTypes = {
  onSuccess: PropTypes.func, // Optional
  onExit: PropTypes.func, // Optional
  children: PropTypes.func.isRequired,
}

export default PlaidLinkWithRetry
